<template>
  <div>
    <v-card class="elevation-0">
      <v-layout pa-4 align-center mb-6 justify-space-between wrap>
        <v-flex xs12 sm8 md4 justify-start mt-2>
          <v-text-field
            v-if="clientSearch"
            v-model="search"
            append-icon="search"
            :label="searchLabel"
            single-line
            dense
            outlined
            hide-details
          />
          <v-text-field
            v-if="useDebouncedSearch"
            v-model="debouncedSearch"
            :label="searchLabel"
            single-line
            hide-details
            outlined
            dense
            @input="emitUpdatedQuery"
            append-icon="search"
          >
            <template v-slot:append-outer>
              <v-menu
                v-if="advancedSearchOptions"
                v-model="advancedFilterMenu"
                :close-on-content-click="false"
                :nudge-width="200"
                offset-x
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" text icon v-bind="attrs" v-on="on">
                    <v-icon x-large>filter_list</v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Advanced Search</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon>
                          <v-icon>search</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <v-divider></v-divider>
                  <v-list>
                    <v-list-item
                      v-for="(option, index) in advancedSearchOptions"
                      :key="index"
                    >
                      <div>
                        <v-list-item-action>
                          <component
                            :is="option.component"
                            v-model="advancedFilter[option.key]"
                            :label="option.label"
                            :items="option.items"
                            :item-text="option.text"
                            :item-value="option.value"
                            outlined
                            dense
                          />
                        </v-list-item-action>
                      </div>
                    </v-list-item>
                  </v-list>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="advancedFilterMenu = false"
                      >Cancel</v-btn
                    >
                    <v-btn color="primary" text @click="onApplyAdvanceFilter"
                      >Apply</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-menu>
            </template>
          </v-text-field>
        </v-flex>
        <v-flex xs12 sm8 md2 justify-end v-if="primaryActionText" mt-2>
          <v-btn @click="$emit('primary-action-event')" color="primary">
            {{ primaryActionText }}
            <!-- <v-icon class="mx-2">{{ primaryActionIcon }}</v-icon> -->
          </v-btn>
        </v-flex>
      </v-layout>
      <v-divider />
      <v-data-table
        :headers="headers"
        :items="items"
        :hide-default-footer="hideFooter"
        :hide-default-header="hideHeader"
        :no-data-text="noDataText"
        :items-per-page="itemsPerPage"
        :search="search"
        :server-items-length="totalDocs"
        @pagination="onUpdatePagination"
        @update:page="onUpdatePage"
        @update:sort-by="onUpdateSortBy"
        @update:sort-desc="onUpdateSortDesc"
      >
        <template v-slot:item.action="{ item }">
          <v-btn
            @click="action.click(item)"
            class="mr-2 ml-2"
            :color="action.color"
            v-for="(action, index) in actions"
            :key="index"
            small
          >
            {{ action.text }}
            <v-icon>{{ action.icon }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { VSelect } from "vuetify/lib";

export default {
  name: "Table",
  props: {
    advancedSearchOptions: {
      type: Array,
      required: false,
    },
    useDebouncedSearch: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => {
        return [];
      },
    },
    searchLabel: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      required: true,
    },
    tableTitle: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
    },
    actions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    clientSearch: {
      type: Boolean,
      default: true,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: "No data available.",
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    filterOptions: {
      type: Array,
      default: () => {
        return [];
      },
    },
    totalDocs: {
      type: Number,
    },
    primaryActionText: {
      type: String,
    },
    primaryActionIcon: {
      type: String,
    },
  },
  data() {
    return {
      advancedFilterMenu: false,
      search: "",
      filterBy: null,
      debouncedSearch: "",
      advancedFilter: {},
    };
  },
  components: {
    VSelect,
  },
  methods: {
    onApplyAdvanceFilter() {
      this.advancedFilterMenu = false;
      this.$emit("applyAdvanceFilter", this.advancedFilter);
    },
    onUpdateSortBy(e) {
      this.$emit("updatedSortBy", e[0]);
    },
    onUpdateSortDesc(e) {
      this.$emit("updateSortDesc", e[0]);
    },
    emitUpdatedQuery: debounce(function () {
      const { debouncedSearch: searchTerm } = this;
      const query = {
        searchTerm,
      };
      this.$emit("updatedSearchVal", query);
    }, 500),
    onUpdatePagination(pagination) {
      if (pagination) this.$emit("updated-pagination", pagination);
    },
    onUpdatePage(page) {
      this.$emit("updated-page", page);
    },
  },
};
</script>

<style scoped>
/* TODO - combine this css style */
.v-data-table >>> .v-data-table__wrapper td,
.v-data-table >>> .v-data-table__wrapper th {
  padding: 5px;
}

@media all and (max-width: 640px) {
  .v-data-table >>> .v-data-table__wrapper table,
  .v-data-table >>> .v-data-table__wrapper thead,
  .v-data-table >>> .v-data-table__wrapper tbody,
  .v-data-table >>> .v-data-table__wrapper th,
  .v-data-table >>> .v-data-table__wrapper td,
  .v-data-table >>> .v-data-table__wrapper tr {
    display: block;
  }
  .v-data-table >>> .v-data-table__wrapper td {
    height: auto;
    border: none !important;
  }

  .v-data-table >>> .v-data-table__wrapper tr {
    padding: 5px;
  }

  .v-data-table >>> .v-data-table__wrapper .v-data-table__mobile-row__cell {
    text-align: left !important;
  }

  .v-data-table >>> .v-data-table__wrapper tr:nth-of-type(even) {
    background: #eee;
  }

  .v-data-table >>> .v-data-table__wrapper th {
    margin-bottom: 15px;
    border: none !important;
  }
}
</style>
